import type { Cue } from '@core/model/cue.ts';

let currentObj: Cue | undefined;

export type ReaderState = { isLoading: boolean };

export type ReaderStorage = {
  subtitleReaderVoice?: string;
  subtitleReaderRate?: number;
  subtitleReaderVolume?: number;
};

export function initVoices(): void {
  window.speechSynthesis?.getVoices();
}

export function stripSubtitleHtml(rawText: string): string {
  return rawText
    .replaceAll(/<br[^>]*>/g, '')
    .replaceAll(/<[^>]*>/g, '')
    .replaceAll(/&amp;/g, '&')
    .replaceAll(/&lt;/g, '<')
    .replaceAll(/&gt;/g, '>')
    .replaceAll(/&quot;/g, '"')
    .replaceAll(/&lrm;/g, '');
}

/**
 * Start TTS. Returns false when speech did not start (busy / no voices / no lang match)
 * so the caller must not advance the dedupe cursor (extension retries while loading).
 */
export function speak(
  speech: string,
  lang: string,
  readerState: ReaderState,
  storage: ReaderStorage,
  parent: { spoke?: boolean } = {},
): boolean {
  if (readerState.isLoading) return false;
  if (!window.speechSynthesis || !speech || !lang) return false;

  const voices = window.speechSynthesis.getVoices();
  if (!voices || voices.length === 0) return false;

  readerState.isLoading = true;

  const code = lang.toLowerCase().replace(/-.*/, '');
  let globalCode: string | undefined;
  let voice: SpeechSynthesisVoice | undefined;

  for (let i = 0; i < voices.length; i += 1) {
    if (voices[i]?.name === storage.subtitleReaderVoice) {
      voice = voices[i];
      break;
    }
  }

  for (let i = 0; i < voices.length; i += 1) {
    if (voices[i]?.lang.startsWith(code)) {
      globalCode = voices[i]?.lang;
      break;
    }
  }

  if (!globalCode) {
    readerState.isLoading = false;
    return false;
  }

  const message = new SpeechSynthesisUtterance();
  message.text = speech;
  message.lang = globalCode;
  message.rate = storage.subtitleReaderRate ?? 1;
  message.volume = storage.subtitleReaderVolume ?? 1;
  if (voice) message.voice = voice;

  window.speechSynthesis.speak(message);

  let started = false;
  setTimeout(() => {
    if (started) return;
    window.speechSynthesis.cancel();
    readerState.isLoading = false;
  }, 5000);

  message.onstart = () => {
    started = true;
  };
  message.onend = () => {
    parent.spoke = true;
    readerState.isLoading = false;
  };
  message.onerror = () => {
    readerState.isLoading = false;
  };
  return true;
}

export function resetReaderCue(): void {
  currentObj = undefined;
}

export function getReaderCurrentCueForTests(): Cue | undefined {
  return currentObj;
}

/**
 * Speak once per cue while the reader is enabled.
 * When TTS is busy, schedules `onRetry` (extension 100ms poll) without advancing
 * the dedupe cursor so the next cue is not skipped.
 */
export function readerBridge(
  rawText: string,
  subObj: Cue,
  langCode: string,
  enabled: boolean,
  readerState: ReaderState,
  storage: ReaderStorage,
  onRetry?: () => void,
): void {
  if (!enabled) return;
  if (currentObj === subObj) return;

  if (readerState.isLoading) {
    if (onRetry) {
      window.setTimeout(onRetry, 100);
    }
    return;
  }

  const speech = stripSubtitleHtml(rawText);
  if (speak(speech, langCode, readerState, storage)) {
    currentObj = subObj;
  }
}
